import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { TransactionHistory } from "@/components/TransactionHistory";
import AddBalanceButton from "@/components/AddBalanceButton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Wallet, Gauge, Robot, SpeakerHigh, Microphone, ChartLineUp, Brain,
  PhoneCall, ArrowRight, Info, CurrencyDollar, Receipt,
} from "@phosphor-icons/react";

const THEMES = {
  money: {
    section: "border-teal-200/80 dark:border-teal-900/50",
    header: "bg-gradient-to-r from-teal-50 to-emerald-50/80 dark:from-teal-950/40 dark:to-emerald-950/20 border-teal-200/60 dark:border-teal-900/40",
    icon: "bg-teal-600 text-white shadow-sm shadow-teal-600/25",
    accent: "text-teal-800 dark:text-teal-200",
  },
  pricing: {
    section: "border-sky-200/80 dark:border-sky-900/50",
    header: "bg-gradient-to-r from-sky-50 to-cyan-50/70 dark:from-sky-950/40 dark:to-cyan-950/20 border-sky-200/60 dark:border-sky-900/40",
    icon: "bg-sky-600 text-white shadow-sm shadow-sky-600/25",
    accent: "text-sky-800 dark:text-sky-200",
  },
  limits: {
    section: "border-amber-200/80 dark:border-amber-900/50",
    header: "bg-gradient-to-r from-amber-50 to-orange-50/70 dark:from-amber-950/35 dark:to-orange-950/20 border-amber-200/60 dark:border-amber-900/40",
    icon: "bg-amber-600 text-white shadow-sm shadow-amber-600/25",
    accent: "text-amber-900 dark:text-amber-200",
  },
  history: {
    section: "border-slate-200/90 dark:border-slate-700/60",
    header: "bg-gradient-to-r from-slate-50 to-slate-100/80 dark:from-slate-900/50 dark:to-slate-800/30 border-slate-200/70 dark:border-slate-700/50",
    icon: "bg-slate-700 text-white shadow-sm dark:bg-slate-600",
    accent: "text-slate-800 dark:text-slate-200",
  },
};

function SectionCard({ title, subtitle, icon: Icon, children, testid, action, theme = "pricing" }) {
  const t = THEMES[theme] || THEMES.pricing;
  return (
    <section
      className={`rounded-2xl border bg-card card-shadow overflow-hidden ${t.section}`}
      data-testid={testid}
    >
      <div className={`flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3.5 sm:px-5 ${t.header}`}>
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${t.icon}`}>
              <Icon className="w-5 h-5" weight="duotone" />
            </div>
          )}
          <div className="min-w-0">
            <h2 className={`text-base font-semibold leading-tight ${t.accent}`}>{title}</h2>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        {action}
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function PlanTable({ plans, emptyLabel, testid, tone = "emerald" }) {
  const head = tone === "sky"
    ? "bg-sky-50/90 dark:bg-sky-950/40 text-sky-800/80 dark:text-sky-200/70"
    : "bg-emerald-50/90 dark:bg-emerald-950/40 text-emerald-800/80 dark:text-emerald-200/70";
  const wrap = tone === "sky"
    ? "border-sky-200/70 dark:border-sky-900/50"
    : "border-emerald-200/70 dark:border-emerald-900/50";

  if (!plans?.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
        {emptyLabel}
      </p>
    );
  }
  return (
    <div className={`overflow-x-auto rounded-xl border ${wrap}`} data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className={`border-b border-inherit text-left text-xs uppercase tracking-wide ${head}`}>
            <th className="px-3 py-2.5 font-semibold">Plan</th>
            <th className="px-3 py-2.5 font-semibold">Channels</th>
            <th className="px-3 py-2.5 font-semibold text-right">Price</th>
          </tr>
        </thead>
        <tbody className="bg-card/80">
          {plans.map((p) => (
            <tr key={p.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
              <td className="px-3 py-2.5 font-medium">{p.name}</td>
              <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                {p.channels_min} – {p.channels_max}
              </td>
              <td className="px-3 py-2.5 tabular-nums font-semibold text-right">
                {money(p.price_minor)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UsagePriceCard({ icon: Icon, label, value, unit, free, hint, testid, tone }) {
  const tones = {
    sky: "border-sky-200/80 bg-sky-50/70 dark:border-sky-900/50 dark:bg-sky-950/30",
    teal: "border-teal-200/80 bg-teal-50/70 dark:border-teal-900/50 dark:bg-teal-950/30",
    amber: "border-amber-200/80 bg-amber-50/70 dark:border-amber-900/50 dark:bg-amber-950/30",
  };
  const icons = {
    sky: "text-sky-600 dark:text-sky-400",
    teal: "text-teal-600 dark:text-teal-400",
    amber: "text-amber-600 dark:text-amber-400",
  };
  return (
    <div
      className={`rounded-xl border px-3.5 py-3 ${tones[tone] || tones.sky}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className={`h-4 w-4 shrink-0 ${icons[tone] || icons.sky}`} weight="duotone" />
        <span className="text-xs font-semibold truncate text-foreground/80">{label}</span>
      </div>
      <p
        className={`mt-1.5 text-xl font-semibold tabular-nums leading-none ${
          free ? "text-emerald-700 dark:text-emerald-400" : "text-foreground"
        }`}
      >
        {free ? (
          <>
            Free
            <span className="ml-1 text-xs font-normal text-muted-foreground">{unit}</span>
          </>
        ) : (
          <>
            {value == null ? "—" : money(value)}
            {value != null && (
              <span className="ml-1 text-xs font-normal text-muted-foreground">{unit}</span>
            )}
          </>
        )}
      </p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function LimitCard({
  title, value, hint, icon: Icon, tone = "sky", cta, testid,
}) {
  const styles = {
    sky: {
      card: "border-sky-200/90 bg-gradient-to-br from-sky-50 to-white dark:from-sky-950/40 dark:to-card dark:border-sky-900/50",
      icon: "bg-sky-600 text-white",
      value: "text-sky-800 dark:text-sky-200",
    },
    emerald: {
      card: "border-emerald-200/90 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/40 dark:to-card dark:border-emerald-900/50",
      icon: "bg-emerald-600 text-white",
      value: "text-emerald-800 dark:text-emerald-200",
    },
    amber: {
      card: "border-amber-200/90 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/35 dark:to-card dark:border-amber-900/50",
      icon: "bg-amber-500 text-white",
      value: "text-amber-900 dark:text-amber-200",
    },
  };
  const s = styles[tone] || styles.sky;
  const empty = value == null || value === "" || Number(value) <= 0;
  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-3 shadow-sm ${s.card}`}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 shadow-sm ${s.icon}`}>
          <Icon className="w-5 h-5" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            {title}
          </p>
          <p className={`mt-1 text-3xl font-semibold tabular-nums tracking-tight ${s.value}`}>
            {empty ? "0" : String(value)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
        </div>
      </div>
      {cta}
    </div>
  );
}

export default function AccountBilling() {
  const { user } = useAuth();
  const isReseller = user?.role === "reseller";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [txnKey, setTxnKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data: overview } = await api.get("/billing/overview");
      setData(overview);
    } catch (e) {
      try {
        const [bal, prices, limits] = await Promise.all([
          api.get("/billing/balance"),
          api.get("/billing/my-pricing").catch(() => ({ data: null })),
          api.get("/billing/call-limit").catch(() => ({ data: null })),
        ]);
        setData({
          cash_balance_minor: bal.data?.cash_balance_minor || 0,
          currency: bal.data?.currency || "USD",
          limits: {
            ai_call_limit: limits.data?.concurrent_call_limit,
            pd_call_limit: limits.data?.pd_call_limit,
            active_calls: limits.data?.active_calls || 0,
          },
          usage_pricing: prices.data || {},
          pd_plans: [],
          ai_plans: [],
        });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail, err));
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await load();
      setLoading(false);
    })();
  }, [load]);

  const usage = data?.usage_pricing || {};
  const limits = data?.limits || {};
  const balance = data?.cash_balance_minor || 0;
  const lowBalance = !loading && balance < 500;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="account-billing-page">
      <PageHeader
        title="Billing"
        subtitle="Check your balance, see what each channel and AI minute costs, and review purchases."
      />

      {/* Quick color legend */}
      <div className="flex flex-wrap gap-2 text-xs" data-testid="billing-color-legend">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-200 px-2.5 py-1 font-medium">
          <span className="h-2 w-2 rounded-full bg-teal-500" /> Balance
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200 px-2.5 py-1 font-medium">
          <span className="h-2 w-2 rounded-full bg-emerald-500" /> PD pricing
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200 px-2.5 py-1 font-medium">
          <span className="h-2 w-2 rounded-full bg-sky-500" /> AI pricing
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 px-2.5 py-1 font-medium">
          <span className="h-2 w-2 rounded-full bg-amber-500" /> Call limits
        </span>
      </div>

      {/* 1. Balance */}
      <SectionCard
        testid="billing-balance-section"
        theme="money"
        icon={Wallet}
        title="1. Your balance"
        subtitle="Cash available to buy AI / Predictive channels and cover usage."
        action={(
          <AddBalanceButton
            onSubmitted={() => {
              load();
              setTxnKey((k) => k + 1);
            }}
          />
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="rounded-xl border border-teal-200/70 bg-gradient-to-br from-teal-50/90 to-white dark:from-teal-950/30 dark:to-card dark:border-teal-900/40 px-5 py-4 min-w-[220px]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-700/80 dark:text-teal-300/80">
              Available cash
            </p>
            <p
              className={`mt-1 text-4xl font-semibold tabular-nums tracking-tight ${
                lowBalance
                  ? "text-amber-700 dark:text-amber-300"
                  : "text-teal-800 dark:text-teal-200"
              }`}
              data-testid="billing-balance"
            >
              {loading ? "…" : money(balance)}
            </p>
            <p className="mt-2 text-sm text-muted-foreground max-w-lg">
              {lowBalance
                ? "Balance is low — add cash so campaigns are not interrupted."
                : "Add cash anytime. Balance updates after your request is approved."}
            </p>
          </div>
          {lowBalance && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-800 px-3.5 py-2.5 text-sm text-amber-950 dark:text-amber-100 max-w-sm shadow-sm">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" weight="fill" />
              <span>Top up before buying more channels or running AI campaigns.</span>
            </div>
          )}
        </div>
      </SectionCard>

      {/* 2. Pricing */}
      <SectionCard
        testid="billing-pricing-section"
        theme="pricing"
        icon={CurrencyDollar}
        title="2. Pricing guide"
        subtitle="What you pay for Predictive (PD) channels, AI channels, and per-minute AI usage."
      >
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading prices…</p>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-4 lg:grid-cols-2">
              <div
                className="rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50/50 to-card dark:from-emerald-950/25 dark:border-emerald-900/45 p-4 space-y-2.5"
                data-testid="billing-pd-plans"
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-white">
                    <ChartLineUp className="w-4 h-4" weight="duotone" />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                      Predictive (PD) plans
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Predictive / Manual concurrent channels
                    </p>
                  </div>
                </div>
                <PlanTable
                  plans={data?.pd_plans}
                  emptyLabel="No Predictive plans published yet. Contact support for PD pricing."
                  testid="billing-pd-plan-table"
                  tone="emerald"
                />
              </div>

              <div
                className="rounded-xl border border-sky-200/80 bg-gradient-to-br from-sky-50/50 to-card dark:from-sky-950/25 dark:border-sky-900/45 p-4 space-y-2.5"
                data-testid="billing-ai-plans"
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-sky-600 text-white">
                    <Brain className="w-4 h-4" weight="duotone" />
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-sky-900 dark:text-sky-200">
                      AI channel plans
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      VICI Bot / AI Agent concurrent capacity
                    </p>
                  </div>
                </div>
                <PlanTable
                  plans={data?.ai_plans}
                  emptyLabel="No AI channel plans published yet. Contact support for AI capacity pricing."
                  testid="billing-ai-plan-table"
                  tone="sky"
                />
              </div>
            </div>

            <div className="rounded-xl border border-amber-200/70 bg-gradient-to-br from-amber-50/40 to-card dark:from-amber-950/20 dark:border-amber-900/40 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-white">
                  <Robot className="w-4 h-4" weight="duotone" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-amber-950 dark:text-amber-100">
                    Per-minute AI usage
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Charged while AI features run on a live call (on top of channel capacity)
                  </p>
                </div>
              </div>
              <div
                className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5"
                data-testid="billing-usage-pricing"
              >
                <UsagePriceCard
                  testid="billing-price-ai-model"
                  icon={Robot}
                  label="AI Model"
                  value={usage.ai_model_price_minor}
                  unit="/ min"
                  hint="Conversation / bot model time"
                  tone="sky"
                />
                <UsagePriceCard
                  testid="billing-price-tts"
                  icon={SpeakerHigh}
                  label="TTS"
                  value={usage.tts_price_minor}
                  unit="/ min"
                  free={usage.tts_billing_type === "free"}
                  hint="Text-to-speech"
                  tone="teal"
                />
                <UsagePriceCard
                  testid="billing-price-stt"
                  icon={Microphone}
                  label="STT"
                  value={usage.stt_price_minor}
                  unit="/ min"
                  free={usage.stt_billing_type === "free"}
                  hint="Speech-to-text"
                  tone="amber"
                />
              </div>
            </div>
          </div>
        )}
      </SectionCard>

      {/* 3. Current limits */}
      <SectionCard
        testid="billing-limits-section"
        theme="limits"
        icon={Gauge}
        title="3. Your current call limits"
        subtitle="How many calls you can run at the same time. When a call ends, the slot frees up."
      >
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading limits…</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <LimitCard
              testid="billing-ai-call-limit"
              icon={Brain}
              tone="sky"
              title="AI call limit"
              value={limits.ai_call_limit}
              hint="Concurrent AI engine capacity (bots & AI agents)"
              cta={(
                <Button
                  asChild
                  size="sm"
                  className="rounded-lg w-full justify-between bg-sky-600 hover:bg-sky-700 text-white"
                >
                  <Link to="/call-limit" data-testid="billing-buy-ai-limit">
                    Buy AI channels
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              )}
            />
            <LimitCard
              testid="billing-pd-call-limit"
              icon={ChartLineUp}
              tone="emerald"
              title="PD call limit"
              value={limits.pd_call_limit}
              hint="Concurrent Predictive / Manual dialing capacity"
              cta={(
                <Button
                  asChild
                  size="sm"
                  className="rounded-lg w-full justify-between bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Link to="/call-limit" data-testid="billing-buy-pd-limit">
                    Buy PD channels
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              )}
            />
            <LimitCard
              testid="billing-active-calls"
              icon={PhoneCall}
              tone="amber"
              title="Active calls now"
              value={limits.active_calls ?? 0}
              hint="Live calls using your capacity right now"
            />
          </div>
        )}
        {!isReseller && (
          <p className="mt-4 text-xs text-amber-900/80 dark:text-amber-100/80 flex items-start gap-1.5 rounded-lg bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900/40 px-3 py-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" weight="fill" />
            Tip: keep AI call limit above your peak concurrent AI campaigns. Running at 0 stops new AI dials.
          </p>
        )}
      </SectionCard>

      {/* 4. Transaction history */}
      <SectionCard
        testid="billing-history-section"
        theme="history"
        icon={Receipt}
        title="4. Transaction history"
        subtitle="Search payments, channel purchases, and usage charges."
      >
        <TransactionHistory key={txnKey} />
      </SectionCard>
    </div>
  );
}
